import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft,
    faCheckCircle,
    faEnvelope,
    faRotateLeft,
    faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import Layout from '../Layout';
import Footer from '../ui/Footer';
import hmsLogoWhite from '../../assets/images/IMT_logo.png';
import { kycService } from '../../services/kycService';
import { useUserInfo } from '../../contexts/UserContext';
import { getFormSections } from './kycFormSchema';
import {
    READING_TONES,
    analysisOutcome,
    readingSummary,
} from './kycDocumentReading';
import ReadingProgress from '../ui/ReadingProgress';
import './KYC.css';

const STATUS_BADGES = {
    invited: 'Invited',
    in_progress: 'In Progress',
    submitted: 'Submitted',
    approved: 'Approved',
    rejected: 'Rejected',
};

const EXTRACTION_STATUS_LABELS = {
    idle: 'Not run yet',
    queued: 'Queued',
    running: 'Running',
    done: 'Completed',
    failed: 'Failed',
    unreadable: 'No document could be read',
};

/** How the reader got on with one uploaded file. "Not read" and "read, nothing
 *  relevant in it" are different findings and the reviewer has to be able to
 *  tell them apart before trusting — or redoing — the extraction. */
const READING_LABELS = {
    read: 'Read',
    blank: 'Read — no text',
    unreadable: 'Not read',
    not_analysed: 'Not analysed yet',
    unknown: 'Reading not recorded',
};

const READING_BADGE_CLASSES = {
    ok: 'kyc-badge-approved',
    warn: 'kyc-badge-in_progress',
    bad: 'kyc-badge-rejected',
    muted: 'kyc-badge-invited',
};

/** One compact reading line per document: badge, how much text came back, how
 *  much of it the AI was actually given, how many pages carried any words, how
 *  many form locations came from it, and the reader's own reason when it read
 *  nothing. The three middle facts only appear when they are a finding — a clean
 *  document stays a one-line "Read · 12,480 characters · 3 fields used". */
const DocumentReadingLine = ({ doc, index, fieldCount }) => {
    const {
        state, characters, charactersUsed, pagesAttempted, pagesWithText, message,
    } = readingSummary(doc, index);
    const detail = [];
    if (characters !== null) detail.push(`${characters.toLocaleString()} characters`);
    // A long document is trimmed to fit one analysis, so part of it never reached
    // the AI at all. A reviewer wondering why a fact was missed needs to know.
    if (charactersUsed !== null && characters !== null && charactersUsed < characters) {
        detail.push(`${charactersUsed.toLocaleString()} of them analysed`);
    }
    if (pagesAttempted && pagesWithText !== null && pagesWithText < pagesAttempted) {
        detail.push(`${pagesWithText} of ${pagesAttempted} scanned pages had words`);
    }
    if (state === 'read' || state === 'blank' || state === 'unknown') {
        detail.push(fieldCount > 0
            ? `${fieldCount} field${fieldCount === 1 ? '' : 's'} used`
            : 'no fields used');
    }
    if (message) detail.push(message);

    return (
        <div className="kyc-doc-row-meta kyc-doc-row-reading">
            <span className={`kyc-badge ${READING_BADGE_CLASSES[READING_TONES[state]]}`}>
                {READING_LABELS[state]}
            </span>
            {detail.length > 0 && <span>{detail.join(' · ')}</span>}
        </div>
    );
};

const Value = ({ children }) => {
    // Tickbox fields (Partner Classification) arrive as a list. React would
    // render ['Freight','Bank'] as "FreightBank" with no separator, so join it
    // here rather than at every call site.
    const value = Array.isArray(children) ? children.filter(Boolean).join(', ') : children;
    return value && String(value).trim()
        ? <span className="kyc-review-value">{value}</span>
        : <span className="kyc-review-value kyc-review-empty-value">Not provided</span>;
};

/** Body of one section (fields / table / fixed-table), read-only. */
const renderSectionBody = (section, formData) => {
    const data = formData?.[section.key];
    return (
        <>
            {section.type === 'fields' && (section.fields || []).map((field) => (
                <div
                    className={`kyc-review-field${field.multiline ? ' kyc-review-field-block' : ''}`}
                    key={field.key}
                >
                    <span className="kyc-review-label">{field.label}</span>
                    <Value>{data?.[field.key]}</Value>
                </div>
            ))}
            {section.type === 'table' && (
                Array.isArray(data) && data.length > 0 ? (
                    <table className="kyc-review-table">
                        <thead>
                            <tr>
                                {section.columns.map((column) => (
                                    <th key={column.key}>{column.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((row, index) => (
                                <tr key={index}>
                                    {section.columns.map((column) => (
                                        <td key={column.key}>{row?.[column.key] || '—'}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="kyc-review-field">
                        <Value>{null}</Value>
                    </div>
                )
            )}
            {section.type === 'table' && (section.extraFields || []).map((field) => (
                <div className="kyc-review-field" key={field.key}>
                    <span className="kyc-review-label">{field.label}</span>
                    <Value>{formData?.[`${section.key}__extra`]?.[field.key]}</Value>
                </div>
            ))}
            {section.type === 'fixed-table' && (
                <table className="kyc-review-table">
                    <thead>
                        <tr>
                            <th>Function</th>
                            {section.columns.map((column) => (
                                <th key={column.key}>{column.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {section.rows.map((row) => (
                            <tr key={row.key}>
                                <td style={{ fontWeight: 600 }}>{row.label}</td>
                                {section.columns.map((column) => (
                                    <td key={column.key}>{data?.[row.key]?.[column.key] || '—'}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </>
    );
};

/** Does a section hold any data? Drives the green dot in the navigator. */
const sectionHasData = (formData, section) => {
    const val = formData?.[section.key];
    if (!val) return false;
    if (Array.isArray(val)) return val.some((row) => Object.values(row || {}).some(Boolean));
    if (typeof val === 'object') {
        return Object.values(val).some((v) => (
            v && typeof v === 'object' ? Object.values(v).some(Boolean) : Boolean(v)
        ));
    }
    return Boolean(val);
};

/**
 * Read-only 000 Onboarding form. Every section is rendered in one continuous
 * vertical flow (the page scrolls normally and long sections expand to fit),
 * with a numbered, sticky section navigator that jumps to a section and tracks
 * the one currently in view via scroll-spy.
 */
const FormDataView = ({ formData, formType }) => {
    const sections = useMemo(() => getFormSections(formType), [formType]);
    const chipRefs = useRef({});
    const sectionRefs = useRef({});
    const [activeKey, setActiveKey] = useState(sections[0]?.key);

    // Scroll-spy: highlight the section nearest the top of the viewport. The
    // root margin shrinks the observation band to the upper third so the active
    // section flips as its heading clears the sticky navigator.
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
                if (visible[0]) setActiveKey(visible[0].target.dataset.seckey);
            },
            { rootMargin: '-90px 0px -65% 0px', threshold: 0 },
        );
        sections.forEach((s) => {
            const el = sectionRefs.current[s.key];
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, [sections]);

    // Keep the active chip visible in the (horizontally scrollable) navigator.
    useEffect(() => {
        const chip = chipRefs.current[activeKey];
        if (chip) chip.scrollIntoView({ block: 'nearest', inline: 'center' });
    }, [activeKey]);

    const goTo = (key) => {
        const el = sectionRefs.current[key];
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <div className="kyc-review-form">
            <nav className="kyc-review-secnav" aria-label="Onboarding form sections">
                {sections.map((s, index) => (
                    <button
                        key={s.key}
                        type="button"
                        ref={(el) => { chipRefs.current[s.key] = el; }}
                        className={
                            'kyc-review-secnav-chip'
                            + (s.key === activeKey ? ' is-active' : '')
                            + (sectionHasData(formData, s) ? ' is-filled' : '')
                        }
                        onClick={() => goTo(s.key)}
                        title={s.title}
                    >
                        <span className="kyc-review-secnav-num">{index + 1}</span>
                        <span className="kyc-review-secnav-label">{s.title}</span>
                    </button>
                ))}
            </nav>

            {sections.map((section, index) => (
                <section
                    key={section.key}
                    id={`kyc-sec-${section.key}`}
                    data-seckey={section.key}
                    ref={(el) => { sectionRefs.current[section.key] = el; }}
                    className="kyc-card kyc-review-section"
                >
                    <h3 className="kyc-section-title">
                        <span className="kyc-section-icon">{index + 1}</span>
                        {section.title}
                    </h3>
                    {section.help && (
                        <p className="kyc-review-section-help">{section.help}</p>
                    )}
                    {renderSectionBody(section, formData)}
                </section>
            ))}
        </div>
    );
};

const WRITEBACK_LABELS = {
    partner_type_selections: 'Type of Partner',
    industry_selections: 'Industries',
    creditor_debtor_selections: 'Creditor / Debtor',
    trading_short_name: 'Trading / Short Name',
    company_website_url: 'Company Website',
    tax_registration_numbers: 'Tax registration numbers',
};

/** What the approval actually copied onto the partner profile.
 *
 * The write-back only fills fields the CRM had left EMPTY, so without this the
 * reviewer has no way to tell whether an approval moved anything. "Left
 * untouched" is shown too — a field skipped because an operator already filled
 * it is a decision, not a failure. */
const ProfileWriteBackPanel = ({ writeback }) => {
    const applied = Object.entries(writeback.applied || {});
    const skipped = Object.entries(writeback.skipped || {})
        .filter(([, reason]) => String(reason).startsWith('partner profile already'));

    return (
        <div className="kyc-card">
            <h3 className="kyc-section-title">Applied to Partner Profile</h3>
            {applied.length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: '0.82rem', margin: 0 }}>
                    Nothing was copied — the partner profile already held every field
                    this form can fill.
                </p>
            ) : (
                applied.map(([field, value]) => (
                    <div className="kyc-review-field" key={field}>
                        <span className="kyc-review-label">
                            {WRITEBACK_LABELS[field] || field}
                        </span>
                        <Value>
                            {Array.isArray(value)
                                ? value.join(', ')
                                : (typeof value === 'object'
                                    ? Object.entries(value).map(([k, v]) => `${k}: ${v}`).join(' · ')
                                    : value)}
                        </Value>
                    </div>
                ))
            )}
            {skipped.length > 0 && (
                <p style={{ color: '#6b7280', fontSize: '0.78rem', margin: '0.6rem 0 0' }}>
                    Left untouched (the profile already had a value):{' '}
                    {skipped.map(([field]) => WRITEBACK_LABELS[field] || field).join(', ')}.
                </p>
            )}
        </div>
    );
};

/** Sanctions Declaration evidence panel: ceremony status, chain + seal
 *  verification on demand, and the paper-scan fallback upload. */
const DeclarationPanel = ({ kycCase, onCaseUpdate }) => {
    const summary = kycCase.sanctions_declaration || { status: 'not_issued' };
    const [verifying, setVerifying] = useState(false);
    const [verdict, setVerdict] = useState(null);
    const [panelError, setPanelError] = useState('');
    const [scanBusy, setScanBusy] = useState(false);
    const scanInputRef = useRef(null);

    const signed = ['signed', 'signed_manual'].includes(summary.status);

    const handleVerify = async () => {
        setPanelError('');
        setVerifying(true);
        setVerdict(null);
        try {
            setVerdict(await kycService.verifyDeclaration(kycCase.id));
        } catch (err) {
            setPanelError(err.response?.data?.error || 'Verification failed.');
        } finally {
            setVerifying(false);
        }
    };

    const handleScanUpload = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        setPanelError('');
        setScanBusy(true);
        try {
            onCaseUpdate(await kycService.uploadDeclarationScan(kycCase.id, file));
        } catch (err) {
            setPanelError(err.response?.data?.error || 'Upload failed.');
        } finally {
            setScanBusy(false);
        }
    };

    const sealBadge = () => {
        if (summary.status === 'signed_manual') {
            return <span className="kyc-badge kyc-badge-in_progress">Paper scan on file</span>;
        }
        if (summary.seal_status === 'sealed') {
            return <span className="kyc-badge kyc-badge-approved">Signed · sealed</span>;
        }
        if (summary.status === 'signed') {
            return <span className="kyc-badge kyc-badge-in_progress">Signed · NOT sealed</span>;
        }
        return <span className="kyc-badge kyc-badge-invited">Awaiting signature</span>;
    };

    const verdictBool = (value) => {
        if (value === true) return <span className="kyc-badge kyc-badge-approved">VALID</span>;
        if (value === false) return <span className="kyc-badge kyc-badge-rejected">INVALID</span>;
        return <span className="kyc-badge kyc-badge-in_progress">CANNOT ESTABLISH</span>;
    };

    return (
        <div className="kyc-card">
            <h3 className="kyc-section-title">Sanctions Declaration</h3>
            <div className="kyc-review-field">
                <span className="kyc-review-label">Status</span>
                <span className="kyc-review-value">{sealBadge()}</span>
            </div>
            {signed && summary.signer_name && (
                <div className="kyc-review-field">
                    <span className="kyc-review-label">Signed by</span>
                    <Value>{summary.signer_name}</Value>
                </div>
            )}
            {signed && summary.signed_at && (
                <div className="kyc-review-field">
                    <span className="kyc-review-label">Signed at</span>
                    <Value>{new Date(summary.signed_at).toLocaleString()}</Value>
                </div>
            )}
            {!signed && (
                <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                    The counterparty signs electronically in the KYC portal (step 3).
                    The pack cannot be submitted without it. If they signed on paper,
                    file the scan below.
                </p>
            )}

            {verdict && (
                <>
                    <div className="kyc-review-field">
                        <span className="kyc-review-label">Audit trail</span>
                        <span className="kyc-review-value">
                            {verdictBool(verdict.chain?.chain_valid)}
                            {verdict.chain?.reason ? ` — ${verdict.chain.reason}` : ''}
                        </span>
                    </div>
                    <div className="kyc-review-field">
                        <span className="kyc-review-label">Document seal</span>
                        <span className="kyc-review-value">
                            {verdict.seal
                                ? (
                                    <>
                                        {verdictBool(verdict.seal.valid)}
                                        {verdict.seal.reason ? ` — ${verdict.seal.reason}` : ''}
                                    </>
                                )
                                : 'No stored document to check yet.'}
                        </span>
                    </div>
                </>
            )}
            {panelError && <div className="kyc-form-error">{panelError}</div>}

            <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                    type="button"
                    className="kyc-btn-secondary"
                    disabled={verifying || !signed}
                    onClick={handleVerify}
                    title="Recompute the hash-chained audit trail and validate the PAdES seal on the stored PDF against the IMT signing certificate"
                >
                    {verifying ? 'Verifying…' : 'Verify Evidence'}
                </button>
                {summary.status !== 'signed' && (
                    <>
                        <button
                            type="button"
                            className="kyc-btn-secondary"
                            disabled={scanBusy}
                            onClick={() => scanInputRef.current?.click()}
                        >
                            {scanBusy ? 'Uploading…' : 'Upload Signed Scan (PDF)'}
                        </button>
                        <input
                            ref={scanInputRef}
                            type="file"
                            accept="application/pdf"
                            style={{ display: 'none' }}
                            onChange={handleScanUpload}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

const KYCCaseReview = () => {
    const history = useHistory();
    const { caseId } = useParams();
    const { userInfo } = useUserInfo();
    // Review actions (approve/reject/reopen) are gated to KYC Approvers on the
    // backend; hide the buttons for everyone else. Renewal / SA-request are
    // separate actions and stay available to all CRM staff.
    const canReview = Boolean(userInfo?.is_kyc_approver || userInfo?.is_superuser);
    const [kycCase, setKycCase] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notes, setNotes] = useState('');
    const [acting, setActing] = useState(false);
    const [inviteInfo, setInviteInfo] = useState(null);

    const loadCase = useCallback(async () => {
        setLoading(true);
        try {
            const data = await kycService.getCase(caseId);
            setKycCase(data);
            setNotes(data.review_notes || '');
        } catch (err) {
            setError('Could not load this KYC case.');
        } finally {
            setLoading(false);
        }
    }, [caseId]);

    useEffect(() => {
        loadCase();
    }, [loadCase]);

    // The analysis runs in the background (reading a scanned pack takes minutes),
    // so check back while it is queued or running instead of waiting on the click.
    const analysing = ['queued', 'running'].includes(kycCase?.extraction_status);
    useEffect(() => {
        if (!analysing) return undefined;
        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                const data = await kycService.getCase(caseId);
                if (!cancelled) setKycCase(data);
            } catch (err) {
                // A missed check is retried on the next render cycle; nothing to show.
                if (!cancelled) setKycCase((current) => (current ? { ...current } : current));
            }
        }, 5000);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [analysing, caseId, kycCase]);

    const handleReview = async (action) => {
        if (action === 'reject' && !notes.trim()) {
            setError('Please add review notes explaining the rejection.');
            return;
        }
        setActing(true);
        setError('');
        try {
            const data = await kycService.reviewCase(caseId, action, notes);
            setKycCase(data);
        } catch (err) {
            setError(err.response?.data?.error || 'Review action failed.');
        } finally {
            setActing(false);
        }
    };

    const handleResendInvite = async () => {
        setActing(true);
        setError('');
        try {
            const data = await kycService.resendInvite(caseId);
            setInviteInfo(data);
        } catch (err) {
            setError(err.response?.data?.error || 'Could not resend the invite.');
        } finally {
            setActing(false);
        }
    };

    const handleRunExtraction = async () => {
        setActing(true);
        setError('');
        try {
            const data = await kycService.runExtraction(caseId);
            setKycCase(data);
        } catch (err) {
            setError(err.response?.data?.error || 'AI extraction failed.');
        } finally {
            setActing(false);
        }
    };

    const handleScreenUbos = async () => {
        setActing(true);
        setError('');
        try {
            const data = await kycService.screenUbos(caseId);
            setKycCase(data);
        } catch (err) {
            setError(err.response?.data?.error || 'UBO screening failed.');
        } finally {
            setActing(false);
        }
    };

    const handleRequestSA = async () => {
        setActing(true);
        setError('');
        try {
            const data = await kycService.requestSA(caseId);
            setKycCase(data);
        } catch (err) {
            setError(err.response?.data?.error || 'Could not upgrade to KYC SA.');
        } finally {
            setActing(false);
        }
    };

    const handleStartRenewal = async () => {
        setActing(true);
        setError('');
        try {
            const data = await kycService.startRenewal(caseId);
            // route param changes -> loadCase re-runs for the new case
            history.push(`/dashboard/crm/kyc/case/${data.case.id}`);
            setInviteInfo({ invite_link: data.invite_link, email_sent: data.email_sent });
        } catch (err) {
            setError(err.response?.data?.error || 'Could not start the renewal.');
        } finally {
            setActing(false);
        }
    };

    // Reading result of the last run: how many of the uploaded files the reader
    // got through, and how many form locations each one produced.
    const aiData = kycCase?.ai_extraction_data || {};
    const extraction = analysisOutcome({
        documents: kycCase?.documents || [],
        aiPayload: aiData,
        extractionStatus: kycCase?.extraction_status,
        filledFieldCount: (aiData.filled_fields || []).length,
    });
    const fieldsPerDocument = {};
    Object.values(aiData.provenance || {}).forEach((info) => {
        const source = info && info.source;
        if (source) fieldsPerDocument[source] = (fieldsPerDocument[source] || 0) + 1;
    });

    return (
        <Layout showLeftNavSidebar={true} showRightSidebar={false}>
            <div className="kyc-case-review">
                <div className="kyc-header">
                    <div className="kyc-title-section">
                        <img src={hmsLogoWhite} alt="IMT Bergbau AG" className="kyc-header-logo-img" />
                        <div>
                            <h1 className="hms-screen-title">
                                {loading ? 'KYC Case' : kycCase?.company_name}
                            </h1>
                            <p className="kyc-subtitle">
                                {loading ? '' : (
                                    <>
                                        KYC {kycCase?.year} · {kycCase?.form_type_display} ·{' '}
                                        <span className={`kyc-badge kyc-badge-${kycCase?.status}`}>
                                            {STATUS_BADGES[kycCase?.status] || kycCase?.status}
                                        </span>
                                    </>
                                )}
                            </p>
                        </div>
                    </div>
                    <div className="kyc-header-right">
                        <button
                            type="button"
                            className="kyc-back-link"
                            onClick={() => history.push('/dashboard/crm/kyc')}
                        >
                            <FontAwesomeIcon icon={faArrowLeft} /> All KYC Cases
                        </button>
                    </div>
                </div>

                <div className="kyc-content">
                    {loading ? (
                        <div className="kyc-loading">Loading case…</div>
                    ) : !kycCase ? (
                        <div className="kyc-card"><div className="kyc-form-error">{error || 'Case not found.'}</div></div>
                    ) : (
                        <div className="kyc-review-grid">
                            <div>
                                {kycCase.care_of_status && !kycCase.care_of_status.has_own_case && (
                                    <div className="kyc-card" style={{ borderColor: '#fcd34d', background: '#fffbeb' }}>
                                        <strong style={{ color: '#b45309' }}>
                                            ⚠ Care-of company "{kycCase.care_of_status.name}" has no KYC case of its own.
                                        </strong>
                                        <p style={{ color: '#92400e', fontSize: '0.85rem', margin: '0.4rem 0 0' }}>
                                            Per the onboarding policy, a care-of company (vessel manager /
                                            operator buying on behalf) must be onboarded separately before
                                            this partner can trade through it.
                                        </p>
                                    </div>
                                )}
                                <FormDataView formData={kycCase.form_data} formType={kycCase.form_type} />
                            </div>
                            <div className="kyc-review-rail">
                                {error && <div className="kyc-form-error">{error}</div>}

                                <div className="kyc-card">
                                    <h3 className="kyc-section-title">Review</h3>
                                    <div className="kyc-review-field">
                                        <span className="kyc-review-label">Invited by</span>
                                        <Value>{kycCase.invited_by_name}</Value>
                                    </div>
                                    <div className="kyc-review-field">
                                        <span className="kyc-review-label">Submitted</span>
                                        <Value>
                                            {kycCase.submitted_at
                                                ? new Date(kycCase.submitted_at).toLocaleString()
                                                : ''}
                                        </Value>
                                    </div>
                                    {kycCase.reviewed_at && (
                                        <div className="kyc-review-field">
                                            <span className="kyc-review-label">Reviewed</span>
                                            <Value>
                                                {`${new Date(kycCase.reviewed_at).toLocaleString()} by ${kycCase.reviewed_by_name || '—'}`}
                                            </Value>
                                        </div>
                                    )}
                                    <div className="kyc-field" style={{ marginTop: '0.75rem' }}>
                                        <label>Review Notes</label>
                                        <textarea
                                            rows={3}
                                            value={notes}
                                            onChange={(event) => setNotes(event.target.value)}
                                            placeholder="Notes for approval / rejection…"
                                        />
                                    </div>
                                    <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                                        {canReview && kycCase.status === 'submitted' && (
                                            <>
                                                <button
                                                    type="button"
                                                    className="kyc-btn-success"
                                                    disabled={acting}
                                                    onClick={() => handleReview('approve')}
                                                >
                                                    <FontAwesomeIcon icon={faCheckCircle} /> Approve
                                                </button>
                                                <button
                                                    type="button"
                                                    className="kyc-btn-danger"
                                                    disabled={acting}
                                                    onClick={() => handleReview('reject')}
                                                >
                                                    <FontAwesomeIcon icon={faTimesCircle} /> Reject
                                                </button>
                                            </>
                                        )}
                                        {canReview && (kycCase.status === 'submitted' || kycCase.status === 'rejected' || kycCase.status === 'approved') && (
                                            <button
                                                type="button"
                                                className="kyc-btn-secondary"
                                                disabled={acting}
                                                onClick={() => handleReview('reopen')}
                                                title="Send the case back to the partner for changes"
                                            >
                                                <FontAwesomeIcon icon={faRotateLeft} /> Reopen for Partner
                                            </button>
                                        )}
                                        {kycCase.status === 'approved' && (
                                            <button
                                                type="button"
                                                className="kyc-btn-secondary"
                                                disabled={acting}
                                                onClick={handleStartRenewal}
                                                title={`Open the ${kycCase.year + 1} KYC case and re-invite the partner contact`}
                                            >
                                                Start {kycCase.year + 1} Renewal
                                            </button>
                                        )}
                                        {kycCase.form_type === 'STANDARD' && (
                                            <button
                                                type="button"
                                                className="kyc-btn-secondary"
                                                disabled={acting}
                                                onClick={handleRequestSA}
                                                title="Upgrade to KYC South Africa — the partner keeps everything entered and is asked for the additional SA sections and documents"
                                            >
                                                Request KYC SA Details
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="kyc-card">
                                    <h3 className="kyc-section-title">AI Extraction</h3>
                                    <div className="kyc-review-field">
                                        <span className="kyc-review-label">Status</span>
                                        <Value>
                                            {EXTRACTION_STATUS_LABELS[kycCase.extraction_status]
                                                || kycCase.extraction_status}
                                        </Value>
                                    </div>
                                    {kycCase.ai_extraction_data?.last_run && (
                                        <div className="kyc-review-field">
                                            <span className="kyc-review-label">Last run</span>
                                            <Value>{new Date(kycCase.ai_extraction_data.last_run).toLocaleString()}</Value>
                                        </div>
                                    )}
                                    {/* Shown even at nought: zero AI-filled locations after a
                                        finished run is a finding, not a reason to hide the row. */}
                                    <div className="kyc-review-field">
                                        <span className="kyc-review-label">AI-filled locations</span>
                                        <Value>{String((kycCase.ai_extraction_data?.filled_fields || []).length)}</Value>
                                    </div>
                                    {(kycCase.documents || []).length > 0
                                        && extraction.counts.unknown < kycCase.documents.length && (
                                        <div className="kyc-review-field">
                                            <span className="kyc-review-label">Documents read</span>
                                            <Value>
                                                {`${extraction.counts.read + extraction.counts.blank} of `
                                                    + `${kycCase.documents.length}`
                                                    + (extraction.unreadableCount > 0
                                                        ? ` · ${extraction.unreadableCount} could not be read`
                                                        : '')}
                                            </Value>
                                        </div>
                                    )}
                                    {extraction.kind === 'unread' && (
                                        <div className="kyc-form-error" style={{ margin: '0.6rem 0 0' }}>
                                            Nothing was read from any uploaded document, so the form was
                                            pre-filled from nothing. See the per-document reason under
                                            Documents below before asking the partner for new copies.
                                        </div>
                                    )}
                                    {/* The reason the run stored for itself. Without this the only
                                        record of why a run produced nothing is the server log, and
                                        the reviewer has nothing to report. */}
                                    {(extraction.kind === 'unread' || extraction.kind === 'error')
                                        && (extraction.failureReason || kycCase.ai_extraction_data?.last_error) && (
                                        <div className="kyc-review-field">
                                            <span className="kyc-review-label">Reason</span>
                                            <Value>
                                                {extraction.failureReason
                                                    || kycCase.ai_extraction_data.last_error}
                                            </Value>
                                        </div>
                                    )}
                                    {(kycCase.ai_extraction_data?.completeness?.questions || []).length > 0 && (
                                        <>
                                            <p style={{ color: '#6b7280', fontSize: '0.8rem', margin: '0.6rem 0 0.3rem' }}>
                                                Open items the partner still owes:
                                            </p>
                                            <ul style={{ margin: 0, paddingLeft: '1.1rem', color: '#374151', fontSize: '0.82rem' }}>
                                                {kycCase.ai_extraction_data.completeness.questions.map((question, index) => (
                                                    <li key={index}>{question}</li>
                                                ))}
                                            </ul>
                                        </>
                                    )}
                                    <div style={{ marginTop: '0.75rem' }}>
                                        <button
                                            type="button"
                                            className="kyc-btn-secondary"
                                            disabled={acting || analysing || (kycCase.documents || []).length === 0}
                                            onClick={handleRunExtraction}
                                        >
                                            {acting ? 'Working…' : analysing ? 'Reading documents…' : 'Run AI Extraction'}
                                        </button>
                                        {analysing && (
                                            <div style={{ marginTop: '0.75rem' }}>
                                                <ReadingProgress
                                                    compact
                                                    progress={kycCase.reading_progress}
                                                    leaveText="You can leave this case — the reading continues and the results appear here."
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {kycCase.ai_extraction_data?.profile_writeback && (
                                    <ProfileWriteBackPanel
                                        writeback={kycCase.ai_extraction_data.profile_writeback}
                                    />
                                )}

                                <div className="kyc-card">
                                    <h3 className="kyc-section-title">UBO Sanctions Screening</h3>
                                    {kycCase.ubo_screening_data?.screened_at ? (
                                        <>
                                            <div className="kyc-review-field">
                                                <span className="kyc-review-label">Screened</span>
                                                <Value>{new Date(kycCase.ubo_screening_data.screened_at).toLocaleString()}</Value>
                                            </div>
                                            <div className="kyc-review-field">
                                                <span className="kyc-review-label">Result</span>
                                                <span className="kyc-review-value">
                                                    {kycCase.ubo_screening_data.suspect_count > 0 ? (
                                                        <span className="kyc-badge kyc-badge-rejected">
                                                            {kycCase.ubo_screening_data.suspect_count} suspect hit(s)
                                                        </span>
                                                    ) : kycCase.ubo_screening_data.error ? (
                                                        <span className="kyc-badge kyc-badge-in_progress">Error</span>
                                                    ) : (
                                                        <span className="kyc-badge kyc-badge-approved">No hits</span>
                                                    )}
                                                </span>
                                            </div>
                                            {kycCase.ubo_screening_data.error && (
                                                <div className="kyc-form-error">{kycCase.ubo_screening_data.error}</div>
                                            )}
                                            {(kycCase.ubo_screening_data.results || []).length > 0 && (
                                                <table className="kyc-review-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Name</th>
                                                            <th>Role</th>
                                                            <th>Score</th>
                                                            <th>Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {kycCase.ubo_screening_data.results.map((row, index) => (
                                                            <tr key={index}>
                                                                <td>{row.name}</td>
                                                                <td>{row.role}</td>
                                                                <td>{row.score}</td>
                                                                <td>
                                                                    {row.suspect ? (
                                                                        <span className="kyc-badge kyc-badge-rejected">
                                                                            HIT{row.hit_list ? ` · ${row.hit_list}` : ''}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="kyc-badge kyc-badge-approved">Clear</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            )}
                                        </>
                                    ) : (
                                        <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                                            Screens every UBO and director on the form against
                                            sanctions lists (Humpert & Kneer — same software as
                                            partner screening).
                                        </p>
                                    )}
                                    <div style={{ marginTop: '0.75rem' }}>
                                        <button
                                            type="button"
                                            className="kyc-btn-primary"
                                            disabled={acting}
                                            onClick={handleScreenUbos}
                                        >
                                            {acting ? 'Working…' : 'Screen UBOs & Directors'}
                                        </button>
                                    </div>
                                </div>

                                <DeclarationPanel kycCase={kycCase} onCaseUpdate={setKycCase} />

                                <div className="kyc-card">
                                    <h3 className="kyc-section-title">
                                        Documents ({kycCase.documents?.length || 0})
                                    </h3>
                                    {(kycCase.documents || []).length === 0 && (
                                        <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                                            No documents uploaded yet.
                                        </p>
                                    )}
                                    {(kycCase.documents || []).map((doc) => (
                                        <div className="kyc-doc-row" key={doc.id}>
                                            <div>
                                                <div className="kyc-doc-row-name">{doc.original_filename}</div>
                                                <div className="kyc-doc-row-meta">
                                                    {doc.doc_type_display}
                                                    {doc.uploaded_by ? ` · ${doc.uploaded_by}` : ''}
                                                    {doc.uploaded_at ? ` · ${new Date(doc.uploaded_at).toLocaleDateString()}` : ''}
                                                </div>
                                                <DocumentReadingLine
                                                    doc={doc}
                                                    index={extraction.index}
                                                    fieldCount={fieldsPerDocument[doc.original_filename] || 0}
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                className="kyc-doc-download-btn"
                                                onClick={() => kycService.downloadDocument(doc.id, doc.original_filename)}
                                            >
                                                Download
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                <div className="kyc-card">
                                    <h3 className="kyc-section-title">Partner Access</h3>
                                    {(kycCase.portal_users || []).map((user) => (
                                        <div className="kyc-review-field" key={user.id}>
                                            <span className="kyc-review-label">{user.full_name || 'Contact'}</span>
                                            <Value>
                                                {`${user.email}${user.invite_accepted_at ? ' · active' : ' · invite pending'}`}
                                            </Value>
                                        </div>
                                    ))}
                                    <div style={{ marginTop: '0.75rem' }}>
                                        <button
                                            type="button"
                                            className="kyc-btn-secondary"
                                            disabled={acting}
                                            onClick={handleResendInvite}
                                        >
                                            <FontAwesomeIcon icon={faEnvelope} /> Resend Invite
                                        </button>
                                    </div>
                                    {inviteInfo && (
                                        <>
                                            {inviteInfo.email_sent ? (
                                                <div className="kyc-form-success" style={{ marginTop: '0.75rem' }}>
                                                    Invite email sent.
                                                </div>
                                            ) : (
                                                <div className="kyc-form-error" style={{ marginTop: '0.75rem' }}>
                                                    Email could not be sent — share this link directly:
                                                </div>
                                            )}
                                            <div className="kyc-invite-link-box">
                                                <input
                                                    type="text"
                                                    readOnly
                                                    value={inviteInfo.invite_link}
                                                    onFocus={(event) => event.target.select()}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <Footer />
            </div>
        </Layout>
    );
};

export default KYCCaseReview;
